<template>
  <div class="my-5 w-screen m-auto px-0">
    <div class="max-w-screen-xl m-auto px-4">
      <Heading
        :level="1"
        h-class="text-h5  leading-5 lg:text-h4"
        :title="eventGetters.getTitle(eventData)"
        class="kft-heading--left"
        data-test-id="event-title"
      />
      <div
        class="text-thin text-base my-2 md:line-clamp-2 md:hover:line-clamp-none"
        v-html="eventGetters.getSubtitle(eventData)"
      ></div>
      <div class="flex justify-between items-center">
        <EventMetaInfo
          v-if="!isGiftcard"
          :supplier-name="supplierNameToDisplay"
          :reviews-average="reviewsAverageToDisplay"
          :reviews-type="reviewsTypeToDisplay"
          :reviews-total="reviewsTotalToDisplay"
          :has-review-section="shouldShowReviews"
        />
        <EventShareWishlistButtons
          :event="eventData"
          :is-giftcard="isGiftcard"
        />
      </div>
      <EventGallery class="mt-5" :event="eventData" :is-giftcard="isGiftcard" />

      <!-- BEGIN::EVENT CONTENT - DESCRIPTION, CHECKOUT, ETC -->
      <div
        v-if="!props.isPreview"
        class="flex flex-col lg:flex-row-reverse gap-4"
      >
        <!--
          Placement is CSS-only on purpose — see the matching comment in
          EventDetails.vue. The origin never receives User-Agent behind
          CloudFront, so a JS device check renders the desktop slot for
          everyone during SSR and only relocates on hydration.
        -->
        <EventDetailsSidebar ref="sidebarRef" class="order-2 lg:order-none" />
        <div class="contents lg:block lg:w-auto lg:flex-1 lg:overflow-hidden">
          <div class="order-1 lg:order-none overflow-hidden">
            <LazyVisibleEventImportantDetailsChips
              v-if="eventData"
              hydrate-on-visible
              :event-data="eventData as EventDescription"
              :is-giftcard="isGiftcard"
            />
            <LazyVisibleEventHighlights
              v-if="eventGetters.getHighlight(eventData).length"
              hydrate-on-visible
              :highlight="eventGetters.getHighlight(eventData)"
            />
            <EventImportantDetails
              v-if="eventData"
              :is-giftcard="isGiftcard"
              :event-data="eventData as EventDescription"
              :items="EVENT_IMPORTANT_DETAIL_ALL_ITEMS"
            />
          </div>
          <div class="order-3 lg:order-none overflow-hidden">
            <div class="w-full my-8">
              <!-- BEGIN::Private Event Info -->
              <NuxtLazyHydrate when-visible>
                <LazyPrivateEventInfo
                  v-if="
                    eventGetters.getIsTeamEvent(eventData) &&
                    eventGetters.getPrivateEventDescription(eventData) !== ''
                  "
                  :description="
                    eventGetters.getPrivateEventDescription(eventData)
                  "
                />
              </NuxtLazyHydrate>
              <!-- END::Private Event Info -->
            </div>
            <EventAddOnsDetails
              v-if="addOns && addOns.length > 0"
              :add-directly-to-cart="true"
              :add-ons="addOns"
              @click:addon="handleAddOnClick"
            />
            <!-- Begin::Ticket suggestion cards -->

            <!-- Remove ticket card showcase temporarily, including header
        <div v-if="!isGiftcard" class="my-10">
          <Heading
            :level="2"
            h-class="text-h3 font-semibold"
            :title="$t('product.ticketCardsSection.title')"
            class="mb-7 kft-heading--left"
          />
          <TicketCardShowcase
            :dates="[]"
            @click:ticket-card-cta="handleTicketCardClick"
          >
          </TicketCardShowcase>

        </div>
        -->
            <!-- END::Ticket suggestion cards -->
            <div
              v-if="!isGiftcard"
              id="sectionPartner"
              ref="partnerSection"
              class="mt-10 w-full bg-stone-50 p-4 rounded-lg"
            >
              <!-- {{ eventGetters.getSupplier(eventData) }} -->
              <NuxtLink
                :to="
                  localePath(
                    `/p/${eventGetters.getSupplierPermalink(eventData)}`
                  )
                "
              >
                <NuxtLazyHydrate when-visible>
                  <UserProfileHeader
                    :title-level="2"
                    title-h-class="text-h5"
                    :subtitle="eventGetters.getSupplierUserName(eventData)"
                    :name="eventGetters.getSupplierName(eventData)"
                    :image-url="eventGetters.getSupplierLogo(eventData)"
                  />
                </NuxtLazyHydrate>
              </NuxtLink>

              <div
                class="text-sm line-clamp-3 mb-6"
                v-html="eventGetters.getSupplierShortDescription(eventData)"
              ></div>
            </div>
          </div>
        </div>
      </div>
      <!-- END::EVENT CONTENT - DESCRIPTION, CHECKOUT, ETC -->
    </div>

    <div
      v-if="shouldShowReviews"
      id="sectionReviews"
      class="product-show-cases !max-w-none py-5 md:py-10 bg-stone-75 mt-10 scroll-mt-[130px] md:scroll-mt-[160px]"
    >
      <NuxtLazyHydrate when-visible>
        <LazyReviewShowcase
          :average-rating="String(reviewsAverageToDisplay)"
          :review-count="reviewsTotalToDisplay"
          :supplier-name="supplierNameToDisplay"
          :reviews="reviewsToDisplay"
          :review-type="reviewsTypeToDisplay"
          :supplier-created-at="supplierCreatedAtToDisplay"
          :display-item-title="false"
          :event-permalink="props.eventPermalink"
          :review-breakdown="eventGetters.getReviewBreakdown(eventData)"
        />
      </NuxtLazyHydrate>
    </div>
    <div class="max-w-screen-xl m-auto px-4">
      <!-- Begin::Supplier events -->
      <div class="product-show-cases">
        <NuxtLazyHydrate when-visible>
          <EventHorizontalProductShowcase
            v-if="parsedSupplierEvents.length > 0 && eventData"
            data-test-id="supplier-events"
            :products="parsedSupplierEvents"
            :gtm-item-list-id="GtmItemListId.EventPageSameSupplierClasses"
            :title="
              isGiftcard
                ? $t('product.giftcardEventsFromTheSameSupplier')
                : $t('product.eventsFromTheSameSupplier')
            "
            algolia-insights-label="Supplier Events - Kit Page"
            :loader-products-count="parsedSupplierEvents.length || 6"
          />
        </NuxtLazyHydrate>
        <!-- End::Supplier events -->

        <!-- Begin::Similar Box Events -->
        <div v-if="!isSubdomain && eventData">
          <NuxtLazyHydrate when-visible>
            <EventSimilarBoxEvents
              :category="eventGetters.getMainCategorySlug(eventData)"
              :title="$t('product.boxSimilarLabel')"
            />
          </NuxtLazyHydrate>
        </div>
        <!-- End::Similar Box Events -->

        <NuxtLazyHydrate when-visible>
          <EventSimilarOnlineEvents
            v-if="!isGiftcard && !isSubdomain && eventData"
            algolia-insights-label="Similar Online Events - Kit Page"
          />
        </NuxtLazyHydrate>
      </div>
      <!-- End::Similar Online events -->
      <EventTags v-if="!isSubdomain && eventData" :event="eventData" />

      <NuxtLazyHydrate when-visible>
        <Breadcrumbs :breadcrumbs="breadcrumbs" />
      </NuxtLazyHydrate>
    </div>
    <EventStickyBuyBoxButton
      v-if="!props.isPreview"
      :visible="isSidebarVisible"
      @scroll-to-buybox="scrollToSidebar"
    />
  </div>
</template>

<script lang="ts" setup>
import * as qs from 'qs'
import type {
  EventDate,
  EventDescription,
  Supplier,
} from '~/composables/types/event'
import type { KftApiDefaultResponse } from '~/composables/types/kft-api'
import useSeoHelper from '~/composables/useSeoHelper'
import useKitProductSchema from '~/composables/schemaOrg/useKitProductSchema'
import { useAlgoliaConversion } from '~/composables/algolia/useAlgoliaConversion'
import { sendProductDetailView } from '~/composables/useGtmEvents'
import { GtmItemListId } from '~/composables/useKftGtm/types'
import {
  useClearEventDataOnUnmount,
  useEventRedirects,
} from '~/composables/useEvent'
import { useIsSubdomain } from '~/composables/useSubdomain'
import { setLastViewedEvent } from '~/utils/lastViewedEvents'
import { supplierGetters } from '~/utils/supplierGetters'
import { hasAddToCartCategoryQuantities } from '~/utils/parseAddToCartCategoryQuantitiesFromQuery'
import { EVENT_IMPORTANT_DETAIL_ALL_ITEMS } from '~/utils/eventImportantDetails'
import { filterAddOnsByMinTickets } from '~/utils/cartAddOnHelpers'

interface Props {
  eventPermalink?: string
  isGiftcard?: boolean
  isSpecialGiftcard?: boolean
  shouldAddEventSchema?: boolean
  isPreview?: boolean
}
const props = withDefaults(defineProps<Props>(), {
  eventPermalink: undefined,
  isGiftcard: false,
  isSpecialGiftcard: false,
  shouldAddEventSchema: false,
  isPreview: false,
})
const isSubdomain = useIsSubdomain()
const route = useRoute()

const { t: $t } = useI18n()
const $localePath = useLocalePath()
const localePath = $localePath
const { query: queryParams } = useRoute()
const { cart, addItem } = useCartWithSubdomainLogic()
const { toggleCartSidebar } = useUiState()
const { sendAddToCartEvent } = useAlgoliaConversion()
const partnerSection = ref()

// Mobile sticky buy-box bar wiring, shared with EventDetails.
const sidebarRef = useTemplateRef<any>('sidebarRef')
const { isSidebarVisible, scrollToSidebar } = useStickyBuyBox(sidebarRef)
const { data: eventDataResponse, error: eventDataLoadError } =
  await useKftApiFetch<KftApiDefaultResponse<EventDescription>>(
    `v1/store/events/${props.eventPermalink}${
      props.isPreview ? `/preview` : ``
    }?${qs.stringify(
      {
        include: [
          'address.locality',
          'supplier',
          'supplier.logo',
          'reviews',
          'activePromotion',
        ],
      },
      { arrayFormat: 'comma' }
    )}`,
    {
      transform: (res) => {
        delete res?.data?.supplier?.data?.description
        return res
      },
    }
  )

const eventData = useEventData()
eventData.value = eventDataResponse.value?.data
useClearEventDataOnUnmount()

// handle event redirects
await useEventRedirects(
  eventData?.value,
  eventDataLoadError?.value,
  props.isGiftcard,
  props.eventPermalink
)

const breadcrumbs = computed(() => {
  return [
    {
      text: $t('general.home'),
      link: localePath({ name: 'index' }),
    },
    {
      text: $t('product.boxEventLabel'),
      link: localePath({ name: 'kits' }),
    },
    {
      text: eventGetters.getTitle(eventData.value),
      link: localePath({
        name: 'b-boxPermalink',
        params: { boxPermalink: eventGetters.getPermalink(eventData.value) },
      }),
    },
  ]
})

provide('eventBuyBoxVisibleElements', {
  calendar: false,
  requestButtons: false,
  giftcard: true,
})
provide('eventData', eventData.value)
provide('isSpecialGiftcardEvent', false)

/**  DATA FETCHING **/
const supplierId = computed(() => eventGetters.getSupplierId(eventData.value))
const hasEventReviews = computed(() => {
  if (!eventData.value) return false
  return (
    eventGetters.getReviewsListRecentFirst(eventData.value).length > 0 &&
    eventGetters.getReviewsTotal(eventData.value) > 0
  )
})

const partnerReviewsFetchResult =
  eventData.value && supplierId.value && !hasEventReviews.value
    ? await useKftApiFetch<KftApiDefaultResponse<Supplier>>(
        `v1/store/supplier/${supplierId.value}?include=reviews`
      )
    : null

const partnerReviewsData = computed(() => {
  if (partnerReviewsFetchResult?.data?.value?.data) {
    return partnerReviewsFetchResult.data.value.data
  }
  return null
})

const hasPartnerReviews = computed(() => {
  if (!partnerReviewsData.value) return false
  const totalReviews = supplierGetters.getReviewsTotal(partnerReviewsData.value)
  const avgReview = supplierGetters.getReviewsAverage(partnerReviewsData.value)
  return totalReviews > 0 && avgReview > 0
})

const hasThirdPartyReviews = computed(() => {
  const partner =
    eventGetters.getSupplier(eventData.value) || partnerReviewsData.value
  return (
    partner?.google_avg_review != null &&
    partner?.google_avg_review > 0 &&
    partner?.google_total_reviews != null &&
    partner?.google_total_reviews > 0
  )
})

const { data: partnerEvents = ref([]) } =
  eventGetters.getSupplierId(eventData.value) &&
  useFetchSupplierEvents(eventGetters.getSupplierId(eventData.value), null, {
    transform: transformToAlgoliaEvent,
    default: () => ref([]),
  })

// special Giftcards are also Online events, this condition covers both cases
// const { data: similarEvents = ref([]) } =
//   eventGetters.getAttendanceMode(eventData.value) !== 'ONLINE'
//     ? useFetchSimilarEvents(eventGetters.getPermalink(eventData.value), {
//         transform: transformToAlgoliaEvent,
//         default: () => [],
//       })
//     : { data: ref([]) }

const {
  add_event_to_cart_quantity: quantity,
  add_event_to_cart_date_id: dateId,
  date_type: paramDateType,
} = queryParams

const eventId = eventGetters.getId(eventData?.value)

const { data: addToCardDate, execute: executeFetchAddToCartDate } =
  await useKftApiFetch<KftApiDefaultResponse<EventDate[]>, EventDate>(
    `v1/store/events/${eventId}/dates?search=id:${dateId};${isSubdomain.value ? 'embeddedPublished:1' : 'konfettiPublished:1'}&include=children`,
    {
      server: false,
      immediate: false,
      transform: (res) => {
        return res?.data && res.data[0]
      },
    }
  )

const { data: addOnsResponse } = await useKftApiFetch(
  `v1/store/events/${eventId}/add-ons?include=media`
)
// handleAddOnClick always adds this addon to the cart at quantity 1
const addOns = computed(() =>
  filterAddOnsByMinTickets(addOnsResponse.value?.data ?? [], 1)
)

/** COMPUTED **/
const parsedSupplierEvents = computed(() => {
  return transformToAlgoliaEvent(partnerEvents.value)
    .filter((e) => e.hashed_id !== eventData.value.id)
    .slice(0, 10)
})

const reviewsToDisplay = computed(() => {
  if (hasEventReviews.value) {
    return eventGetters.getReviewsListRecentFirst(eventData.value)
  }
  if (hasPartnerReviews.value) {
    return supplierGetters.getReviewsList(partnerReviewsData.value)
  }
  return []
})

const reviewsTypeToDisplay = computed(() => {
  if (hasEventReviews.value) return 'event'
  if (hasPartnerReviews.value) return 'partner'
  if (hasThirdPartyReviews.value) return 'third-party'
  return 'event'
})

const reviewsAverageToDisplay = computed(() => {
  if (hasEventReviews.value) {
    return eventGetters.getReviewsAverage(eventData.value) || 0
  }
  if (hasPartnerReviews.value) {
    return String(supplierGetters.getReviewsAverage(partnerReviewsData.value))
  }
  const partner =
    eventGetters.getSupplier(eventData.value) || partnerReviewsData.value
  if (partner?.google_avg_review) {
    return String(partner.google_avg_review)
  }
  return 0
})

const reviewsTotalToDisplay = computed(() => {
  if (hasEventReviews.value) {
    return eventGetters.getReviewsTotal(eventData.value)
  }
  if (hasPartnerReviews.value) {
    return supplierGetters.getReviewsTotal(partnerReviewsData.value)
  }
  const supplier =
    eventGetters.getSupplier(eventData.value) || partnerReviewsData.value
  if (supplier?.google_total_reviews) {
    return supplier.google_total_reviews
  }
  return 0
})

const supplierNameToDisplay = computed(() => {
  return eventGetters.getSupplierName(eventData.value) || ''
})

const supplierCreatedAtToDisplay = computed(() => {
  if (hasEventReviews.value) {
    return eventGetters.getSupplierCreatedAt(eventData.value)
  }
  if (hasPartnerReviews.value) {
    return supplierGetters.getCreatedAt(partnerReviewsData.value)
  }
  return ''
})

const shouldShowReviews = computed(() => hasEventReviews.value)

const shouldTryToFetchAddToCartLinks = computed(
  () =>
    quantity &&
    dateId &&
    eventId &&
    !hasAddToCartCategoryQuantities(queryParams) &&
    !cart.value.items.find(
      (i) => i.id === dateId && i.quantity === Number(quantity)
    )
)

const handleAddToCartLinks = async () => {
  const qtyTickets = Math.min(
    Number(quantity),
    addToCardDate.value?.availableTicketsQuantity || 1
  )

  if (addToCardDate.value && addToCardDate.value?.dateType && eventData.value) {
    const { dateType } = addToCardDate.value
    const dateIsPrivate = dateType === 'PRIVATE' || paramDateType === 'PRIVATE'

    const pricingsResponse = await useFetchPricings(ref(eventData.value.id), {
      dateId: ref(addToCardDate.value.id),
      quantity: ref(qtyTickets),
      isPrivate: ref(dateIsPrivate),
    })

    if (!pricingsResponse) {
      return
    }

    await book(
      parseEventDateToCartItem(
        eventData.value,
        qtyTickets,
        pricingsResponse,
        addToCardDate as Ref<EventDate>,
        ref([]),
        dateIsPrivate
      )
    )
  }
}

const book = async (params) => {
  await addItem(params)
  toggleCartSidebar()
}

const { script: productSchemas } = useKitProductSchema([eventData.value])

const handleAddOnClick = async (addOn) => {
  const queryId = route.query.qid?.toString() || null
  scrollToSidebar()
  // for kits, if it's available, we can already add to cart.
  // If we don't get next instance, there's no inventory do nothing more
  const selectedDate = toRef(eventGetters.getNextInstance(eventData.value))
  if (selectedDate.value) {
    const pricingsResponse = await useFetchPricings(ref(eventData.value.id), {
      dateId: ref(selectedDate.value.id),
      quantity: ref(1),
      isPrivate: ref(false),
    })

    if (!pricingsResponse) {
      return
    }

    const cartItem = parseEventDateToCartItem(
      eventData.value,
      1,
      pricingsResponse,
      selectedDate,
      toRef(addOn),
      false,
      queryId
    )

    if (cartItem) {
      await addItem(cartItem)
      toggleCartSidebar()
    }
    sendAddToCartEvent(eventData)
  }
}

onMounted(async () => {
  if (shouldTryToFetchAddToCartLinks.value) {
    await executeFetchAddToCartDate()
    handleAddToCartLinks()
  }

  if (eventData.value) {
    sendProductDetailView(eventData.value)
  }

  setLastViewedEvent(eventData.value?.id)
})

const seoOptions = computed(() => ({
  head: {
    script: [productSchemas],
    title: eventGetters.getTitle(eventData.value),
  },
  shouldBeIndexed: !props.isPreview,

  meta: {
    title:
      eventGetters.getTitle(eventData.value) +
      ' | ' +
      $t('general.titles.base'),
    'og:title':
      eventGetters.getTitle(eventData.value) +
      ' | ' +
      $t('general.titles.base'),
    description: eventGetters.getMetaDescription(eventData.value),
    'og:description': eventGetters.getMetaDescription(eventData.value),
    ...(eventGetters.getOpenGraphImage(eventData.value) && {
      'og:image': eventGetters.getOpenGraphImage(eventData.value),
    }),
    'og:type': 'product',
    ...(eventGetters.getMinimumPriceAmount(eventData.value) && {
      'product:price:amount':
        Number(eventGetters.getMinimumPriceAmount(eventData.value)) / 100,
    }),
    'product:price:currency': eventGetters.getMinimumPriceCurrency(
      eventData.value
    ),
    'og:availability': 'instock',
  },
}))
useSeoHelper(seoOptions)
</script>
